import { useMemo, useState } from 'react';
import { ChevronDown, FileText, FileUp, Heading2, Plus, X } from 'lucide-react';
import SearchBar from './SearchBar';
import {
  CUSTOM_QUESTION_TYPE_OPTIONS,
  publicFormCatalogGroupTitle,
  publicFormFieldTypeTitle,
  publicFormTargetSupportsUploads,
} from '../utils/publicFormBuilder';

const PublicFormFieldPalette = ({
  catalog = [],
  selectedFields = [],
  onAdd,
  onAddCustom,
  onAddContent = () => {},
  onAddUpload,
  customQuestionsEnabled = false,
  uploadsEnabled = false,
  targetMode,
}) => {
  const [query, setQuery] = useState('');
  const [customQuestionType, setCustomQuestionType] = useState('text');
  const uploadsSupported = publicFormTargetSupportsUploads(targetMode);
  const selectedKeys = useMemo(
    () => new Set(selectedFields.map((field) => String(field.field_key || ''))),
    [selectedFields],
  );
  const groups = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return catalog.reduce((result, field) => {
      if (normalizedQuery && !`${field.label} ${field.group}`.toLowerCase().includes(normalizedQuery)) return result;
      const group = field.group || 'Other';
      if (!result[group]) result[group] = [];
      result[group].push(field);
      return result;
    }, {});
  }, [catalog, query]);

  return (
    <aside className="public-form-field-palette" aria-label="Available form fields">
      <div className="sub-title public-form-palette-heading">
        <h4>Add fields</h4>
      </div>
      <div className="public-form-palette-search">
        <div className="form-group nm public-form-palette-search-control">
          <SearchBar
            expanded
            value={query}
            setSearchQuery={setQuery}
            placeholder="Search fields"
            ariaLabel="Search available fields"
          />
        </div>
        {query && (
          <button
            type="button"
            className="action-micro quaternary"
            onClick={() => setQuery('')}
            aria-label="Clear field search"
          >
            <X size={15} aria-hidden="true" />
          </button>
        )}
      </div>

      <div className="public-form-palette-scroll">
        {Object.keys(groups).length === 0 && (
          <p className="public-form-empty-copy">No writable fields match this search.</p>
        )}
        {Object.entries(groups).map(([group, fields]) => (
          <section className="public-form-palette-group" key={group}>
            <div className="sub-title"><h4>{publicFormCatalogGroupTitle(group)}</h4></div>
            {fields.map((field) => {
              const selected = selectedKeys.has(String(field.field_key || field.id));
              return (
                <button
                  type="button"
                  key={field.id}
                  className="action quaternary public-form-palette-field"
                  onClick={() => onAdd(field)}
                  disabled={selected}
                  aria-label={`${selected ? 'Added' : 'Add'} ${field.label}`}
                >
                  <span>
                    <strong>{field.label}</strong>
                    <small>{publicFormFieldTypeTitle(field.type)}</small>
                  </span>
                  <Plus size={15} aria-hidden="true" />
                </button>
              );
            })}
          </section>
        ))}

        {customQuestionsEnabled && (
          <section className="public-form-palette-option public-form-custom-question-option">
            <div>
              <div className="sub-title"><h4>Your questions</h4></div>
              <p className="subtext">Add a question that is not connected to a Reports field.</p>
            </div>
            <div className="form-group filter-group nm public-form-palette-type">
              <label className="subtext" htmlFor="public-form-custom-question-type">Question type</label>
              <div className="select-wrapper">
                <select
                  id="public-form-custom-question-type"
                  className="filter-select"
                  value={customQuestionType}
                  onChange={(event) => setCustomQuestionType(event.target.value)}
                >
                  {CUSTOM_QUESTION_TYPE_OPTIONS.map((option) => (
                    <option value={option.value} key={option.value}>{option.label}</option>
                  ))}
                </select>
                <ChevronDown size={16} className="select-chevron" aria-hidden="true" />
              </div>
            </div>
            <button type="button" className="action-tiny quaternary" onClick={() => onAddCustom(customQuestionType)}>
              Add custom question
            </button>
          </section>
        )}

        <section className="public-form-palette-option public-form-content-option">
          <div>
            <div className="sub-title"><h4>Section content</h4></div>
            <p className="subtext">Add headings and guidance in the exact order recipients should read them. These items do not collect data.</p>
          </div>
          <div className="public-form-content-actions">
            <button type="button" className="action-tiny quaternary" onClick={() => onAddContent('subtitle')}>
              <Heading2 size={15} aria-hidden="true" /> Add subtitle
            </button>
            <button type="button" className="action-tiny quaternary" onClick={() => onAddContent('instruction')}>
              <FileText size={15} aria-hidden="true" /> Add instructions
            </button>
          </div>
        </section>

        {uploadsEnabled === true && (
          <section className="public-form-palette-option public-form-upload-option">
            <FileUp size={18} aria-hidden="true" />
            <div>
              <div className="sub-title"><h4>Document upload</h4></div>
              <p className="subtext">{uploadsSupported
                ? 'Recipients can choose approved documents or photos from their device.'
                : targetMode === 'create_case'
                  ? 'Document uploads are not available for forms that create a new case yet.'
                  : 'Choose an existing case, an existing lead, or a new lead to add document uploads.'}</p>
            </div>
            {uploadsSupported && (
              <button type="button" className="action-tiny quaternary" onClick={onAddUpload}>
                Add document upload
              </button>
            )}
          </section>
        )}
      </div>
    </aside>
  );
};

export default PublicFormFieldPalette;
